import React, { useState } from 'react';
import { X, Calendar as CalendarIcon, Clock, User, Home, Phone, Mail, CheckCircle } from 'lucide-react';

const CalendarModal = ({ 
  isOpen, 
  onClose, 
  propertyTitle,
  propertyId,
  contactPerson,
  contactPhone 
}) => {
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    notes: ''
  });
  const [step, setStep] = useState(1); // 1: Fecha, 2: Hora, 3: Datos, 4: Confirmación
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen) return null;

  // Fechas bloqueadas (ejemplo: fines de semana y algunas fechas específicas)
  const isDateDisabled = (date) => {
    const day = date.getDay();
    // Bloquear domingos (0) y sábados después de las 13:00 si es la fecha actual
    if (day === 0) return true; // Domingo
    
    // Si es sábado, permitir solo hasta las 13:00
    if (day === 6 && selectedTime && parseInt(selectedTime.split(':')[0]) > 13) {
      return true;
    }
    
    // Ejemplo: bloquear fechas específicas (podrías tener esto en una API)
    const blockedDates = [
      '2024-12-25', // Navidad
      '2024-12-31', // Año Nuevo
      '2024-11-15'  // Ejemplo de fecha ocupada
    ];
    
    const dateString = date.toISOString().split('T')[0];
    return blockedDates.includes(dateString);
  };

  // Horarios disponibles
  const timeSlots = [
    '09:00', '09:30', '10:00', '10:30', '11:00', '11:30',
    '12:00', '12:30', '13:00', '13:30', '14:00', '14:30',
    '15:00', '15:30', '16:00', '16:30', '17:00'
  ];

  // Horarios no disponibles para sábados (solo hasta las 13:00)
  const getAvailableTimeSlots = () => {
    if (!selectedDate) return timeSlots;
    
    const day = selectedDate.getDay();
    if (day === 6) { // Sábado
      return timeSlots.filter(time => {
        const hour = parseInt(time.split(':')[0]);
        return hour <= 13; // Solo hasta las 13:00
      });
    }
    
    return timeSlots;
  };

  // Generar fechas para los próximos 30 días
  const generateAvailableDates = () => {
    const dates = [];
    const today = new Date();
    
    for (let i = 1; i <= 30; i++) {
      const date = new Date();
      date.setDate(today.getDate() + i);
      
      if (!isDateDisabled(date)) {
        dates.push(date);
      }
    }
    
    return dates;
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setSelectedTime('');
    setStep(2);
  };

  const handleTimeSelect = (time) => {
    setSelectedTime(time);
    setStep(3);
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || '/api'}/appointments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          propertyId,
          propertyTitle,
          date: selectedDate.toISOString().split('T')[0],
          time: selectedTime,
          ...formData
        })
      });

      if (!response.ok) {
        throw new Error('Error al agendar la cita');
      }

      setStep(4);
    } catch (error) {
      alert('No se pudo agendar la cita. Intentá de nuevo en unos minutos.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('es-AR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const availableDates = generateAvailableDates();

  return (
    <div className="fixed inset-0 z-[60] overflow-y-auto">
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-75 transition-opacity"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="flex items-center justify-center min-h-screen p-4">
        <div className="relative bg-white rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-hidden shadow-2xl">
          {/* Header */}
          <div className="sticky top-0 z-10 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <CalendarIcon className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-foreground">Agendar Visita</h2>
                <p className="text-sm text-muted-foreground">{propertyTitle}</p>
              </div>
            </div>
            
            <button 
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              aria-label="Cerrar"
            >
              <X className="w-6 h-6 text-gray-500" />
            </button>
          </div>
          
          {/* Contenido */}
          <div className="overflow-y-auto max-h-[calc(90vh-80px)] p-6">
            {/* Indicador de pasos */}
            <div className="mb-8">
              <div className="flex justify-between items-center mb-4">
                {[1, 2, 3, 4].map((stepNumber) => (
                  <div key={stepNumber} className="flex flex-col items-center">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 ${
                      step >= stepNumber 
                        ? 'bg-primary border-primary text-white' 
                        : 'bg-gray-100 border-gray-300 text-gray-400'
                    }`}>
                      {stepNumber}
                    </div>
                    <span className="text-xs mt-2">
                      {stepNumber === 1 && 'Fecha'}
                      {stepNumber === 2 && 'Hora'}
                      {stepNumber === 3 && 'Datos'}
                      {stepNumber === 4 && 'Confirmar'}
                    </span>
                  </div>
                ))}
              </div>
              <div className="h-1 bg-gray-200 rounded-full">
                <div 
                  className="h-full bg-primary rounded-full transition-all duration-300"
                  style={{ width: `${(step - 1) * 33.33}%` }}
                />
              </div>
            </div>
            
            {/* Paso 1: Seleccionar fecha */}
            {step === 1 && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold mb-4">Selecciona una fecha disponible</h3>
                  <p className="text-gray-600 mb-6">Selecciona una fecha para visitar la propiedad.</p>
                </div>
                
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {availableDates.map((date, index) => {
                    const isWeekend = date.getDay() === 0 || date.getDay() === 6;
                    const isSaturday = date.getDay() === 6;
                    
                    return (
                      <button
                        key={index}
                        onClick={() => handleDateSelect(date)}
                        className="w-full p-4 text-left border border-gray-200 rounded-xl hover:border-primary hover:bg-primary/5 transition-all flex items-center justify-between group"
                      >
                        <div className="flex items-center gap-4">
                          <div className={`p-3 rounded-lg ${
                            isWeekend ? 'bg-orange-100' : 'bg-blue-100'
                          }`}>
                            <CalendarIcon className={`w-6 h-6 ${
                              isWeekend ? 'text-orange-600' : 'text-blue-600'
                            }`} />
                          </div>
                          <div>
                            <p className="font-semibold text-lg">
                              {date.toLocaleDateString('es-AR', { 
                                weekday: 'long',
                                day: 'numeric',
                                month: 'long'
                              })}
                            </p>
                            <p className="text-gray-600">
                              {isSaturday 
                                ? 'Disponible hasta las 13:00' 
                                : 'Disponible todo el día'}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-medium text-gray-700">
                            {date.toLocaleDateString('es-AR', { weekday: 'short' })}
                          </p>
                          <p className={`text-sm ${
                            isSaturday ? 'text-orange-600' : 'text-green-600'
                          }`}>
                            {isSaturday ? 'Horario reducido' : 'Disponible'}
                          </p>
                        </div>
                      </button>
                    );
                  })}
                </div>
                
                {/* Leyenda */}
                <div className="mt-6 p-4 bg-gray-50 rounded-xl">
                  <p className="text-sm text-gray-600 mb-2">💡 Información importante:</p>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                      <span>Días hábiles: Disponible de 9:00 a 17:00</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
                      <span>Sábados: Disponible solo hasta las 13:00</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                      <span>Domingos: No hay visitas disponibles</span>
                    </li>
                  </ul>
                </div>
              </div>
            )}
            
            {/* Paso 2: Seleccionar hora */}
            {step === 2 && selectedDate && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold mb-2">Selecciona un horario</h3>
                  <p className="text-gray-600 mb-4">
                    Para el {formatDate(selectedDate)}
                  </p>
                  
                  {selectedDate.getDay() === 6 && (
                    <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                      <p className="text-sm text-orange-800">
                        ⚠️ Los sábados solo hay disponibilidad hasta las 13:00
                      </p>
                    </div>
                  )}
                </div>
                
                <div className="grid grid-cols-3 gap-3">
                  {getAvailableTimeSlots().map((time) => {
                    const isSelected = selectedTime === time;
                    const [hour] = time.split(':');
                    const isAfternoon = parseInt(hour) >= 13;
                    
                    return (
                      <button
                        key={time}
                        onClick={() => handleTimeSelect(time)}
                        className={`p-4 rounded-xl border transition-all ${
                          isSelected
                            ? 'border-primary bg-primary text-white'
                            : 'border-gray-200 hover:border-primary hover:bg-primary/5'
                        }`}
                      >
                        <div className="flex flex-col items-center">
                          <Clock className={`w-6 h-6 mb-2 ${
                            isSelected ? 'text-white' : 'text-gray-400'
                          }`} />
                          <span className="font-semibold text-lg">{time}</span>
                          <span className={`text-sm mt-1 ${
                            isSelected ? 'text-white/90' : 'text-gray-500'
                          }`}>
                            {isAfternoon ? 'Tarde' : 'Mañana'}
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>
                
                <div className="flex gap-3 mt-6">
                  <button
                    onClick={() => setStep(1)}
                    className="px-6 py-3 border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors"
                  >
                    Volver
                  </button>
                  <button
                    onClick={() => selectedTime && setStep(3)}
                    disabled={!selectedTime}
                    className={`px-6 py-3 rounded-xl transition-colors flex-1 ${
                      selectedTime
                        ? 'bg-primary text-white hover:bg-primary/90'
                        : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    Continuar
                  </button>
                </div>
              </div>
            )}
            
            {/* Paso 3: Ingresar datos */}
            {step === 3 && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold mb-2">Tus datos de contacto</h3>
                  <p className="text-gray-600 mb-6">
                    {selectedDate && selectedTime && (
                      <>Cita para el <span className="font-semibold">{formatDate(selectedDate)}</span> a las <span className="font-semibold">{selectedTime}</span></>
                    )}
                  </p>
                </div>
                
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">Nombre completo *</label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          required
                          className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                          placeholder="Tu nombre"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">Teléfono *</label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          required
                          className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                          placeholder="+54 11 1234-5678"
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">Email *</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                        placeholder="tu@email.com"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">Notas adicionales (opcional)</label>
                    <textarea
                      name="notes"
                      value={formData.notes}
                      onChange={handleInputChange}
                      rows="3"
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none"
                      placeholder="Comentarios adicionales o preguntas..."
                    />
                  </div>
                  
                  <div className="bg-blue-50 p-4 rounded-xl">
                    <p className="text-sm text-blue-800">
                      📞 Te contactaremos para confirmar la cita. Recibirás un recordatorio por email.
                    </p>
                  </div>
                  
                  <div className="flex gap-3 pt-4">
                    <button
                      type="button"
                      onClick={() => setStep(2)}
                      className="px-6 py-3 border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors"
                    >
                      Volver
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className={`px-6 py-3 rounded-xl transition-colors flex-1 ${
                        isSubmitting
                          ? 'bg-gray-400 cursor-not-allowed'
                          : 'bg-primary text-white hover:bg-primary/90'
                      }`}
                    >
                      {isSubmitting ? 'Agendando...' : 'Confirmar Cita'}
                    </button>
                  </div>
                </form>
              </div>
            )}
            
            {/* Paso 4: Confirmación */}
            {step === 4 && (
              <div className="text-center space-y-6 py-8">
                <div className="flex justify-center">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-10 h-10 text-green-600" />
                  </div>
                </div>
                
                <div>
                  <h3 className="text-2xl font-bold mb-2">¡Cita Agendada!</h3>
                  <p className="text-gray-600 mb-4">
                    Tu visita ha sido programada exitosamente
                  </p>
                  
                  <div className="bg-gray-50 rounded-xl p-6 max-w-md mx-auto space-y-3">
                    <div className="flex items-center gap-3">
                      <Home className="w-5 h-5 text-gray-400" />
                      <div className="text-left">
                        <p className="text-sm text-gray-500">Propiedad</p>
                        <p className="font-medium">{propertyTitle}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <CalendarIcon className="w-5 h-5 text-gray-400" />
                      <div className="text-left">
                        <p className="text-sm text-gray-500">Fecha y hora</p>
                        <p className="font-medium">
                          {selectedDate && formatDate(selectedDate)} a las {selectedTime}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <User className="w-5 h-5 text-gray-400" />
                      <div className="text-left">
                        <p className="text-sm text-gray-500">Agente asignado</p>
                        <p className="font-medium">{contactPerson}</p>
                        <p className="text-sm text-gray-600">{contactPhone}</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <p className="text-gray-600">
                    Recibirás un email de confirmación con todos los detalles y un recordatorio 24 horas antes.
                  </p>
                  
                  <div className="flex gap-3 justify-center">
                    <button
                      onClick={() => {
                        // Reiniciar el formulario
                        setSelectedDate(null);
                        setSelectedTime('');
                        setFormData({
                          name: '',
                          email: '',
                          phone: '',
                          notes: ''
                        });
                        setStep(1);
                      }}
                      className="px-6 py-3 border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors"
                    >
                      Agendar otra visita
                    </button>
                    <button
                      onClick={onClose}
                      className="px-6 py-3 bg-primary text-white rounded-xl hover:bg-primary/90 transition-colors"
                    >
                      Cerrar
                    </button>
                  </div>
                  
                  <p className="text-sm text-gray-500 mt-6">
                    Si necesitas modificar o cancelar la cita, contáctanos al {contactPhone}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CalendarModal;